import React, { useState, useEffect, useCallback } from 'react';
import { useApi, api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './Commandes.css';
import '../components/ConfirmDeleteModal.css';

// ─── Utilitaires ─────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatMontant(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return n.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

const STATUTS = {
  completed  : { label: 'Complétée',    cls: 'badge--success' },
  processing : { label: 'En traitement',cls: 'badge--warning' },
  cancelled  : { label: 'Annulée',      cls: 'badge--danger'  },
  refunded   : { label: 'Remboursée',   cls: 'badge--danger'  },
  pending    : { label: 'En attente',   cls: 'badge--info'    },
  'on-hold'  : { label: 'En attente',   cls: 'badge--info'    },
};

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || { label: statut, cls: '' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

// ─── Squelette tableau ────────────────────────

function SkeletonTableau({ cols = 8, lignes = 5 }) {
  return (
    <div className="cmd-skel-wrap">
      {[...Array(lignes)].map((_, i) => (
        <div key={i} className="cmd-skel-row">
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="skeleton cmd-skel-cell" style={{ flex: j === 2 ? 2 : 1 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Section 1 — Statut de connexion ─────────

function SectionStatut() {
  const [statut,     setStatut]     = useState(null);
  const [chargement, setChargement] = useState(false);

  const tester = useCallback(async () => {
    setChargement(true);
    try {
      const data = await api.get('/wc/status');
      setStatut(data);
    } catch {
      setStatut({ connected: false, message: 'Erreur de connexion au backend' });
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { tester(); }, [tester]);

  return (
    <div className="card cmd-card rise-in">
      <div className="card-header">
        <div className="card-header-left">
          <span className="card-header-icon"><Icon name="boutique" size={15} /></span>
          <h3 className="card-title">Connexion WooCommerce</h3>
        </div>
        <button className="btn btn-ghost btn--sm" onClick={tester} disabled={chargement}>
          <Icon name="rafraichir" size={14} />
          {chargement ? 'Test en cours…' : 'Tester la connexion'}
        </button>
      </div>

      {statut === null ? (
        <p className="muted" style={{ fontSize: 13 }}>Vérification en cours…</p>
      ) : statut.connected ? (
        <div className="cmd-statut-ligne">
          <span className="cmd-dot cmd-dot--ok" />
          <div>
            <div style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 14 }}>
              Connecté — {statut.name}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{statut.url}</div>
          </div>
        </div>
      ) : (
        <div className="cmd-statut-ligne">
          <span className="cmd-dot cmd-dot--err" />
          <div>
            <div style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 14 }}>Non connecté</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{statut.message}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section 2 — Synchronisation ─────────────

function SectionSync({ onSynced }) {
  const [en_cours,       setEnCours]       = useState(false);
  const [resultat,       setResultat]      = useState(null);
  const [reconcEnCours,  setReconcEnCours] = useState(false);
  const [reconcResultat, setReconcResultat]= useState(null);

  const synchroniser = async () => {
    setEnCours(true);
    setResultat(null);
    try {
      const data = await api.get('/wc/commandes/sync');
      setResultat(data);
      onSynced();
    } catch (err) {
      setResultat({ error: err.response?.data?.error || err.message });
    } finally {
      setEnCours(false);
    }
  };

  const reconcilier = async () => {
    setReconcEnCours(true);
    setReconcResultat(null);
    try {
      const data = await api.post('/wc/reconcilier-stock');
      setReconcResultat(data);
      onSynced();
    } catch (err) {
      setReconcResultat({ error: err.response?.data?.error || err.message });
    } finally {
      setReconcEnCours(false);
    }
  };

  return (
    <div className="card cmd-card rise-in">
      <div className="card-header">
        <div className="card-header-left">
          <span className="card-header-icon"><Icon name="rafraichir" size={15} /></span>
          <h3 className="card-title">Synchronisation</h3>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            className="btn btn-ghost btn--sm"
            onClick={reconcilier}
            disabled={reconcEnCours}
            title="Re-impute le stock pour les commandes existantes (idempotent)"
          >
            <Icon name="recalculer" size={14} />
            {reconcEnCours ? 'Recalcul…' : 'Recalculer stock'}
          </button>
          <button className="btn btn-primary btn--sm" onClick={synchroniser} disabled={en_cours}>
            <Icon name="rafraichir" size={14} />
            {en_cours ? 'Synchronisation…' : 'Synchroniser les commandes'}
          </button>
        </div>
      </div>

      <p className="muted" style={{ fontSize: 13 }}>
        Importe les commandes des 90 derniers jours depuis WooCommerce et crée les écritures comptables manquantes.
      </p>

      {reconcResultat && (
        <div className="cmd-sync-resultat">
          {reconcResultat.error ? (
            <div className="error-inline" style={{ marginTop: 'var(--space-3)' }}>
              <Icon name="erreur" size={14} />
              Erreur recalcul : {reconcResultat.error}
            </div>
          ) : (
            <div className="cmd-sync-pills">
              <div className="cmd-sync-pill">
                <span className="cmd-sync-num muted">{reconcResultat.commandes_traitees}</span>
                <span className="muted" style={{ fontSize: 13 }}>commande(s) traitée(s)</span>
              </div>
              <div className="cmd-sync-pill">
                <span className="cmd-sync-num" style={{ color: 'var(--success)' }}>{reconcResultat.imputations_nouvelles}</span>
                <span className="muted" style={{ fontSize: 13 }}>nouvelle(s) imputation(s) FIFO</span>
              </div>
              {reconcResultat.erreurs?.length > 0 && (
                <div className="error-inline" style={{ fontSize: 12 }}>
                  {reconcResultat.erreurs.join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {resultat && (
        <div className="cmd-sync-resultat">
          {resultat.error ? (
            <div className="error-inline" style={{ marginTop: 'var(--space-3)' }}>
              <Icon name="erreur" size={14} />
              {resultat.error}
            </div>
          ) : (
            <div className="cmd-sync-pills">
              <div className="cmd-sync-pill">
                <span className="cmd-sync-num" style={{ color: 'var(--accent)' }}>{resultat.nouvelles}</span>
                <span className="muted" style={{ fontSize: 13 }}>
                  nouvelle{resultat.nouvelles !== 1 ? 's' : ''} commande{resultat.nouvelles !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="cmd-sync-pill">
                <span className="cmd-sync-num" style={{ color: 'var(--success)' }}>{resultat.ecritures}</span>
                <span className="muted" style={{ fontSize: 13 }}>
                  écriture{resultat.ecritures !== 1 ? 's' : ''} créée{resultat.ecritures !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="cmd-sync-pill">
                <span className="cmd-sync-num muted">{resultat.total_synced}</span>
                <span className="muted" style={{ fontSize: 13 }}>
                  commande{resultat.total_synced !== 1 ? 's' : ''} analysée{resultat.total_synced !== 1 ? 's' : ''}
                </span>
              </div>
              {resultat.erreurs?.length > 0 && (
                <div className="error-inline" style={{ fontSize: 12, flexDirection: 'column', alignItems: 'flex-start' }}>
                  {resultat.erreurs.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Modal frais Stripe ──────────────────────

function fraisStripeFormule(total) {
  return Math.round((parseFloat(total) * 0.029 + 0.30) * 100) / 100;
}

function ModalFraisStripe({ commande, onConfirm, onAnnuler, loading }) {
  const fraisActuel = parseFloat(commande.frais_stripe) || 0;
  const fraisSugg   = fraisStripeFormule(commande.total);
  const [frais,  setFrais]  = useState(String(fraisActuel > 0 ? fraisActuel : fraisSugg));
  const [erreur, setErreur] = useState('');

  const submit = () => {
    setErreur('');
    const v = parseFloat(frais);
    if (!Number.isFinite(v) || v < 0) return setErreur('Frais invalide');
    if (v > parseFloat(commande.total))  return setErreur('Frais > total commande, vérifie');
    onConfirm(v);
  };

  return (
    <div className="cdm-overlay" onClick={onAnnuler}>
      <div className="cdm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3 className="cdm-titre">
          Frais Stripe — <span style={{ color: 'var(--accent)' }}>#{commande.wc_order_id}</span>
        </h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          Total commande : <strong>{formatMontant(commande.total)}</strong> · {commande.client_nom || 'sans client'}
        </p>

        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <div className="field">
            <label>Montant des frais Stripe</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={frais}
              onChange={e => setFrais(e.target.value)}
            />
            <div className="field-hint">
              Suggestion : <button
                type="button"
                className="btn btn-ghost btn--sm"
                style={{ padding: '2px 8px', fontSize: 12 }}
                onClick={() => setFrais(String(fraisSugg))}
              >
                {formatMontant(fraisSugg)} (2,9% + 0,30 $)
              </button>
            </div>
          </div>
          {erreur && (
            <div className="error-inline">
              <Icon name="erreur" size={14} />
              {erreur}
            </div>
          )}
        </div>

        <p className="muted" style={{ fontSize: 12 }}>
          Une écriture <strong>Dépense / Frais Stripe</strong> sera créée (ou mise à jour) dans le
          journal, liée à cette commande. Le revenu reste intact à {formatMontant(commande.total)}.
          {fraisActuel > 0 && ' Si tu mets 0, l\'écriture sera supprimée.'}
        </p>

        <div className="cdm-actions">
          <button className="btn btn-ghost" onClick={onAnnuler} disabled={loading}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'Enregistrement…' : (fraisActuel > 0 ? 'Mettre à jour' : 'Enregistrer')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal exclusion commande ─────────────────

function ModalExclureCommande({ commande, onConfirm, onAnnuler, loading }) {
  return (
    <div className="cdm-overlay" onClick={onAnnuler}>
      <div className="cdm-modal" onClick={e => e.stopPropagation()}>
        <h3 className="cdm-titre">
          Exclure la commande <span style={{ color: 'var(--accent)' }}>#{commande.wc_order_id}</span> ?
        </h3>

        <p className="cdm-intro muted">Tout sera définitivement effacé du cache local :</p>
        <ul className="cdm-liste">
          <li>
            <Icon name="erreur" size={13} color="var(--danger)" />
            Commande {formatMontant(commande.total)} — {commande.client_nom || 'sans client'}
          </li>
          <li>
            <Icon name="erreur" size={13} color="var(--danger)" />
            {commande.ecriture_faite
              ? '1 écriture comptable liée (Revenu Vente Woocommerce)'
              : 'Aucune écriture comptable liée'}
          </li>
        </ul>

        <p className="cdm-wc muted">
          La commande sur WooCommerce n'est pas affectée. Si elle existe encore en ligne,
          la prochaine synchronisation la réimportera.
        </p>

        <p className="cdm-avertissement danger">Cette action est irréversible.</p>

        <div className="cdm-actions">
          <button className="btn btn-ghost" onClick={onAnnuler} disabled={loading}>Annuler</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Suppression…' : 'Exclure définitivement'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section 3 — Liste des commandes ─────────

function SectionCommandes({ refreshKey }) {
  const { data: commandes, loading, error, refetch } = useApi('/wc/commandes');
  const [aSupprimer,          setASupprimer]          = useState(null);
  const [suppressionEnCours,  setSuppressionEnCours]  = useState(false);
  const [erreurSuppression,   setErreurSuppression]   = useState('');
  const [aFraisStripe,        setAFraisStripe]        = useState(null);
  const [fraisEnCours,        setFraisEnCours]        = useState(false);
  const [erreurFrais,         setErreurFrais]         = useState('');

  useEffect(() => { refetch(); }, [refreshKey, refetch]);

  const exclureCommande = async () => {
    if (!aSupprimer) return;
    setSuppressionEnCours(true);
    setErreurSuppression('');
    try {
      await api.delete(`/wc/commandes/${aSupprimer.wc_order_id}`);
      setASupprimer(null);
      refetch();
    } catch (err) {
      setErreurSuppression(err.response?.data?.error || err.message);
    } finally {
      setSuppressionEnCours(false);
    }
  };

  const enregistrerFraisStripe = async (frais) => {
    if (!aFraisStripe) return;
    setFraisEnCours(true);
    setErreurFrais('');
    try {
      await api.post(`/wc/commandes/${aFraisStripe.wc_order_id}/frais-stripe`, { frais });
      setAFraisStripe(null);
      refetch();
    } catch (err) {
      setErreurFrais(err.response?.data?.error || err.message);
    } finally {
      setFraisEnCours(false);
    }
  };

  return (
    <div className="card cmd-card rise-in">
      <div className="card-header">
        <div className="card-header-left">
          <span className="card-header-icon"><Icon name="liste" size={15} /></span>
          <h3 className="card-title">Commandes</h3>
        </div>
        {commandes && (
          <span className="muted" style={{ fontSize: 13 }}>
            {commandes.length} commande{commandes.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading && <SkeletonTableau cols={8} lignes={5} />}

      {error && (
        <div className="error-inline">
          <Icon name="erreur" size={14} />
          {error}
        </div>
      )}

      {erreurSuppression && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-3)' }}>
          <Icon name="erreur" size={14} />
          Erreur suppression : {erreurSuppression}
        </div>
      )}

      {!loading && !error && commandes && commandes.length === 0 && (
        <div className="empty-state">
          <Icon name="panier" size={36} />
          <p className="empty-state__titre">Aucune commande</p>
          <p>Cliquez "Synchroniser les commandes" pour importer depuis WooCommerce.</p>
        </div>
      )}

      {!loading && commandes && commandes.length > 0 && (
        <div className="cmd-table-scroll">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th># Commande WC</th>
                <th>Client</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Écriture comptable</th>
                <th>Frais Stripe</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {commandes.map(c => (
                <tr key={c.id}>
                  <td className="muted">{formatDate(c.date_commande)}</td>
                  <td style={{ color: 'var(--accent)', fontWeight: 600, fontFamily: 'monospace' }}>
                    #{c.wc_order_id}
                  </td>
                  <td>{c.client_nom || '—'}</td>
                  <td style={{ fontFeatureSettings: "'tnum' 1", fontWeight: 600 }}>
                    {formatMontant(c.total)}
                  </td>
                  <td><StatutBadge statut={c.statut} /></td>
                  <td>
                    {c.ecriture_faite
                      ? <span className="badge badge--success">Créée</span>
                      : <span className="badge">Non créée</span>}
                  </td>
                  <td>
                    {parseFloat(c.frais_stripe) > 0 ? (
                      <button
                        className="btn btn-ghost btn--sm"
                        onClick={() => setAFraisStripe(c)}
                        title="Modifier les frais Stripe"
                      >
                        <Icon name="modifier" size={13} />
                        {formatMontant(c.frais_stripe)}
                      </button>
                    ) : c.ecriture_faite ? (
                      <button
                        className="btn btn-ghost btn--sm"
                        onClick={() => setAFraisStripe(c)}
                        title="Saisir les frais Stripe pour cette commande"
                      >
                        <Icon name="ajouter" size={13} />
                        Saisir
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-danger btn--sm"
                      onClick={() => setASupprimer(c)}
                      title="Exclure cette commande et son écriture comptable"
                    >
                      <Icon name="supprimer" size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aSupprimer && (
        <ModalExclureCommande
          commande={aSupprimer}
          onConfirm={exclureCommande}
          onAnnuler={() => { setASupprimer(null); setErreurSuppression(''); }}
          loading={suppressionEnCours}
        />
      )}

      {aFraisStripe && (
        <ModalFraisStripe
          commande={aFraisStripe}
          onConfirm={enregistrerFraisStripe}
          onAnnuler={() => { setAFraisStripe(null); setErreurFrais(''); }}
          loading={fraisEnCours}
        />
      )}

      {erreurFrais && (
        <div className="error-inline" style={{ marginTop: 'var(--space-3)' }}>
          <Icon name="erreur" size={14} />
          Erreur frais Stripe : {erreurFrais}
        </div>
      )}
    </div>
  );
}

// ─── Section 4 — Stock en ligne ───────────────

function SectionStock() {
  const [chargement, setChargement] = useState(false);
  const [donnees,    setDonnees]    = useState(null);

  const rafraichir = async () => {
    setChargement(true);
    try {
      const data = await api.get('/wc/stock');
      setDonnees(data);
    } catch (err) {
      setDonnees({ error: err.response?.data?.error || err.message });
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="card cmd-card rise-in">
      <div className="card-header">
        <div className="card-header-left">
          <span className="card-header-icon"><Icon name="boutique" size={15} /></span>
          <h3 className="card-title">Stock en ligne (WooCommerce)</h3>
        </div>
        <button className="btn btn-ghost btn--sm" onClick={rafraichir} disabled={chargement}>
          <Icon name="rafraichir" size={14} />
          {chargement ? 'Chargement…' : 'Rafraîchir le stock'}
        </button>
      </div>

      {donnees === null && (
        <p className="muted" style={{ fontSize: 13 }}>
          Cliquez "Rafraîchir" pour lire le stock depuis WooCommerce.
        </p>
      )}

      {donnees?.error && (
        <div className="error-inline">
          <Icon name="erreur" size={14} />
          {donnees.error}
        </div>
      )}

      {donnees?.connected === false && (
        <p className="muted">WooCommerce non configuré.</p>
      )}

      {donnees?.produits && donnees.produits.length === 0 && (
        <div className="empty-state">
          <Icon name="boutique" size={36} />
          <p className="empty-state__titre">Aucun produit WooCommerce</p>
          <p>Finalisez un batch pour créer vos premiers produits.</p>
        </div>
      )}

      {donnees?.produits && donnees.produits.length > 0 && (
        <div className="cmd-table-scroll">
          <table className="esc-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Nom produit</th>
                <th style={{ textAlign: 'right' }}>Stock</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Prix</th>
              </tr>
            </thead>
            <tbody>
              {donnees.produits.map(p => (
                <tr key={p.id}>
                  <td style={{ color: 'var(--accent)', fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
                    {p.sku}
                  </td>
                  <td>
                    {p.wc_url
                      ? <a href={p.wc_url} target="_blank" rel="noreferrer" className="cmd-wc-link">{p.nom}</a>
                      : p.nom}
                    {p.erreur && <div style={{ color: 'var(--danger)', fontSize: 11 }}>{p.erreur}</div>}
                  </td>
                  <td style={{ textAlign: 'right', fontFeatureSettings: "'tnum' 1" }}>
                    {p.stock_quantity != null ? p.stock_quantity : '—'}
                  </td>
                  <td>
                    {p.stock_status === 'instock'
                      ? <span className="badge badge--success">En stock</span>
                      : p.stock_status === 'outofstock'
                        ? <span className="badge badge--danger">Épuisé</span>
                        : p.stock_status
                          ? <span className="badge">{p.stock_status}</span>
                          : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontFeatureSettings: "'tnum' 1" }}>
                    {p.prix ? formatMontant(p.prix) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────

export default function Commandes() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="commandes-page">

      {/* ── En-tête ── */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Commandes WooCommerce</h1>
          <p className="page-subtitle muted">Synchronisation bidirectionnelle avec escamel.ca</p>
        </div>
      </div>

      <div className="commandes-grille">
        <SectionStatut />
        <SectionSync onSynced={() => setRefreshKey(k => k + 1)} />
        <SectionCommandes refreshKey={refreshKey} />
        <SectionStock />
      </div>
    </div>
  );
}
